import type { FactionProfileView } from '../api/catalog';

/**
 * A faction's name as a person reads it.
 *
 * <p>A mod's faction may carry no display name, or its own class as one, and
 * the pickers showed `rhs_faction_socom_marsoc` and `gmx_cdf2022` as if they
 * were names (QUICK_START_REVIEW.md F12). A name that reads like a class is
 * turned into words; the class itself stays available for a title attribute.
 */
export function factionLabel(p: Pick<FactionProfileView, 'name' | 'factionClass'>): string {
  const name = (p.name ?? '').trim();
  if (name && !looksLikeClass(name, p.factionClass)) return name;
  return humaniseClass(p.factionClass) || name || p.factionClass;
}

/** True for a "name" that is really a class: the class itself, or words joined by underscores. */
function looksLikeClass(name: string, factionClass: string): boolean {
  return (
    name.toLowerCase() === factionClass.toLowerCase() || /^[A-Za-z0-9]+(_[A-Za-z0-9]+)+$/.test(name)
  );
}

/**
 * `rhs_faction_socom_marsoc` → "SOCOM MARSOC", `gmx_cdf2022` → "CDF2022": the
 * word "faction" and the mod's own prefix go, and what is left is written in
 * capitals, the way the units themselves are named in the lobby.
 */
export function humaniseClass(factionClass: string): string {
  let words = factionClass.split(/[_\s]+/).filter((w) => w && w.toLowerCase() !== 'faction');
  // The prefix goes only when what follows says more than a letter or two.
  if (words.length > 1 && words[0].length <= 7 && words.slice(1).join('').length > 2) {
    words = words.slice(1);
  }
  return words.join(' ').toUpperCase();
}
